/**
 * Worker RPC 契约（任务 6c 起建，后续任务在此追加方法/推送）。
 *
 * 两侧共用：Worker 侧 app.ts 按它实现 handlers，主线程侧 workerHost 按它拿
 * 端到端类型推断。**本文件只允许 type 导入 Worker 模块**——主线程会打包它，
 * 带值导入会把 Worker 专属代码拖进首屏 bundle。
 *
 * 错误约定：Worker 把业务/网络错误折进 RpcError 的 code 字符串跨线程传递
 * （RPC 信封只认 code+message）：`api/<业务码>` / `net/<kind>`。主线程用
 * parseApiErrorCode 还原业务码分支（如 10105 密码错误）。
 */

import type { LoginParams, QrCodeInfo, VerificationCodeType } from './auth/api';
import type { StoredAccountSummary } from './auth/credentials';
import type { AppConfig } from './auth/config';
import type { ApiRouteState } from './net/route';
import type { PushSubscriptionInput } from './push/api';
import type { DeviceListPage, FeedbackListPage, TwoFaSetup } from './user/api';
import type { SubmitFeedbackInput } from './user/service';
import type { ConversationRecord } from './db/conversations-store';
import type { StoredMessage } from './db/messages-store';
import type { ConversationKey } from './ws/messages';
import type { TimelineSlice } from './chat/timeline';
import type { PinnedMessage } from './chat/messages-api';
import type {
  GroupMember,
  GroupMemberLogItem,
  InviteMembersResult,
} from './chat/members-api';
import type { OutboxEntry, OutboxEvent, SendInput } from './chat/outbox';
import type { ForwardResult, ImageSendResult } from './chat/session';
import type { DraftRecord, RestoreAnchor, RestorePoint } from './db/local-state';
import type { ComposerRestriction } from '~/chat/groupRestrictionModel';
import type { GroupChangeField, GroupProfileSnapshot } from '~/chat/groupProfileModel';
import type {
  ContactGroupChat,
  ContactPoints,
  ContactsBook,
  ContactsPush,
} from '~/chat/contactsModel';
import type {
  CreatedStickerPack,
  RecentSticker,
  StickerDraftItem,
  StickerMineSnapshot,
  StickerPack,
  StickerPackDraft,
} from '~/chat/stickerModel';
import type { SearchedGroup, SearchedUser } from '~/chat/contactSearchModel';
import type { PresenceEntry } from '~/chat/presenceModel';
import type { AddFriendInput, GroupApplyInput } from './chat/search-api';
import type { ApplyKind, ContactApply, RespondApplyInput } from '~/chat/applyModel';
import type { BlackListUser, FriendChangeField, FriendProfile } from './chat/friend-api';
import type { AnnouncementItem } from './chat/announcement-api';
import type { MyProfile, ProfileChangeField } from '~/chat/myProfileModel';

// 主线程切片直接消费这组形状（type-only 再导出，不拖 Worker 代码进首屏）
export type {
  ConversationRecord,
  ConversationRemote,
  ConversationLocal,
} from './db/conversations-store';
export type { AppConfig } from './auth/config';
export type { ApiRouteState } from './net/route';
export type { PushSubscriptionInput } from './push/api';
export type { StoredAccountSummary } from './auth/credentials';
export type { DeviceListPage, FeedbackItem, FeedbackListPage, LoginDevice, TwoFaSetup } from './user/api';
export type { SubmitFeedbackInput } from './user/service';
export type { StoredMessage } from './db/messages-store';
export type { ConversationKey } from './ws/messages';
export type { TimelineSlice } from './chat/timeline';
export type { PeerRead, PinnedMessage } from './chat/messages-api';
export type {
  GroupMember,
  GroupMemberLogItem,
  InviteMembersResult,
} from './chat/members-api';
export type { OutboxEntry, OutboxEvent, OutboxStatus, SendInput } from './chat/outbox';
export type { DraftRecord, RestoreAnchor, RestorePoint } from './db/local-state';
export type { ForwardResult } from './chat/session';
export type { ComposerRestriction } from '~/chat/groupRestrictionModel';
export type { GroupChangeField, GroupProfileSnapshot } from '~/chat/groupProfileModel';
export type {
  CreatedStickerPack,
  RecentSticker,
  StickerDraftItem,
  StickerMineSnapshot,
  StickerPack,
  StickerPackDraft,
} from '~/chat/stickerModel';
export type {
  ContactFriend,
  ContactGroupChat,
  ContactGroupSection,
  ContactPoints,
  ContactsBook,
  ContactsPush,
} from '~/chat/contactsModel';
export type { SearchedGroup, SearchedUser } from '~/chat/contactSearchModel';
export type { PresenceEntry, PresenceMap, PresenceState } from '~/chat/presenceModel';
export type { AddFriendInput, GroupApplyInput } from './chat/search-api';
export type { ApplyKind, ContactApply, RespondApplyInput } from '~/chat/applyModel';
export type { BlackListUser, FriendChangeField, FriendProfile } from './chat/friend-api';
export type { AnnouncementItem } from './chat/announcement-api';
export type { MyProfile, PrivacyField, ProfileChangeField } from '~/chat/myProfileModel';

// ---------------------------------------------------------------------------
// 鉴权状态（主线程 GlobalState.auth 的数据源）
// ---------------------------------------------------------------------------

/** 凭证确认失效的原因（http/client.ts onAuthLost 同口径） */
export type AuthLostReason = 'invalid-token' | 'refresh-failed';

export interface AuthUser {
  readonly userId: number;
  readonly userNo: number;
  /** 首登未设密码（验证码登录后 profile.HasPassword=false）——守卫强制跳设密码页（6e-2） */
  readonly needsPassword: boolean;
  /**
   * 展示元数据快照（21n-2）：来自账号记录（`noteProfile` 回写），**零 HTTP**。
   * 桌面侧栏头像用它；还没拉过 profile 的新账号是空串，UI 回落首字母占位。
   * ⚠️ 这是**快照不是权威**——要完整资料仍走 `user/profile/load`。
   */
  readonly nickname: string;
  readonly avatar: string;
}

export type AuthState =
  | { readonly status: 'logged-out' }
  | ({ readonly status: 'logged-in' } & AuthUser);

export type LoginRpcResult =
  | { readonly need2fa: true }
  | { readonly need2fa: false; readonly user: AuthUser };

/** 扫码登录轮询结果（§3.7）。confirmed 时 Worker 已落凭证（与 login 同路径），
 *  扫码必然已设密码（6a 轮核实），不查 profile、needsPassword 恒 false */
export type QrCheckRpcResult =
  | { readonly status: 'pending' }
  | { readonly status: 'expired' }
  | { readonly status: 'confirmed'; readonly user: AuthUser };

/** 账号管理页列表行（14.34a；凭证库记录 + 是否当前账号） */
export interface AccountSummary extends StoredAccountSummary {
  readonly current: boolean;
}

/**
 * 切换账号结果（14.34a）。网络错误原样抛（RpcError net/*，当前账号未动可重试）；
 * ok:false 是终态：not-found=记录不存在；refresh-failed=目标 refreshToken 已
 * 失效（**记录已删**，UI 提示重新登录）。ok:true 时 Worker 已完成整条切换链
 * （凭证落位+旧库清理+新 session 已启），主线程照 auth/login 成功同路径接管。
 */
export type SwitchAccountRpcResult =
  | { readonly ok: true; readonly user: AuthUser }
  | { readonly ok: false; readonly reason: 'not-found' | 'refresh-failed' };

export interface SendCodeParams {
  readonly codeType: VerificationCodeType;
  /** 邮箱 或 区号+手机号（拼接归 UI 层） */
  readonly account: string;
  readonly ticket: string;
  readonly randstr: string;
}

export interface FindPasswordParams {
  /** 1=手机 2=邮箱 */
  readonly type: 1 | 2;
  readonly username: string;
  readonly code: string;
  readonly newPassword: string;
  readonly countryCode?: number;
}

// ---------------------------------------------------------------------------
// 会话列表（任务 8c）
// ---------------------------------------------------------------------------

export interface ConversationsSnapshot {
  /** 已按 compareConversations（isTop→lastMsgTimestamp→lastMsgId 全降序）排好 */
  readonly rows: ConversationRecord[];
}

/**
 * 会话列表推送。upsert = WS 一帧批内变更的会话（主线程按 key 合并重排）；
 * replace = connectok 对账后的全量本地快照（对账整页重写 remote，逐条 diff 不值得）。
 *
 * 顺序正确性：两种推送与 `chat/conversations` 快照响应都产自 Worker 的 DB 写
 * 串行链（chat/session.ts），postMessage 到达序 = 链序 = 数据新旧序——主线程
 * 按到达序应用即可，无需版本号（conversationsState.ts 依赖此约定）。
 */
export type ConversationsPush =
  | { readonly kind: 'replace'; readonly rows: ConversationRecord[] }
  | { readonly kind: 'upsert'; readonly rows: ConversationRecord[] };

// ---------------------------------------------------------------------------
// 聊天时间线（任务 9c）
// ---------------------------------------------------------------------------

/**
 * 打开中会话的时间线推送。
 * - append = WS 一帧批内落库的时间线行（升序）。同 id 行可能重现（43 隐蔽改单
 *   就地替换）——主线程按 id 合并替换，不假设纯追加。
 * - invalidate = 顶部洞补拉完成（缺的行已落库）。主线程以当前已加载行数重调
 *   `chat/timeline/open` 重读合并视图（纯本地零 HTTP），按 id 合并进现有行。
 * - remove = 消息被服务端删除（本端 msg/delete 回包 / 他端与对方的 WS 20 信令，
 *   Worker 本地行已删）。主线程按 id 过滤现有行——invalidate 的重读是按 id 并集
 *   合并，删除必须走显式 remove 才能让行退场。
 * - reset = 本地行被**整体清空**（清聊天记录、删好友、拉黑、35 信令…）。21ad 补：
 *   `invalidate` 在这里是**无效的**——它的重读按 id 并集合并，「行没了」在并集里
 *   表达不出来，界面上消息会一直留着直到刷新（人工实测报的 bug）；`remove` 也不行，
 *   行已经删了拿不到 id 列表。所以单给一种语义：**主线程当场清空视图，不重读**
 *   （本地已空，重读会退化成 msg/list 回服务端拉一页，把刚删的消息又拉回屏上
 *   ——21ad 第二轮人工复测踩的就是这个）。
 *
 * 顺序正确性与 ConversationsPush 同约定：推送与 open/older 响应都产自 Worker
 * 的 DB 写串行链，postMessage 到达序 = 链序——首屏切片未回包时丢弃 append
 * （open 读在链上排在该批之后，回包必含它）。
 */
export type TimelinePush =
  | { readonly kind: 'append'; readonly key: ConversationKey; readonly rows: StoredMessage[] }
  | { readonly kind: 'invalidate'; readonly key: ConversationKey }
  | { readonly kind: 'reset'; readonly key: ConversationKey }
  | { readonly kind: 'remove'; readonly key: ConversationKey; readonly ids: readonly number[] };

// ---------------------------------------------------------------------------
// 发送（任务 10b；状态机口径见 worker/chat/outbox.ts 文件头）
// ---------------------------------------------------------------------------

/**
 * open 回包 = 首屏切片 + 该会话当下的 outbox 条目快照。outbox 快照在首屏读
 * 完成后采集：postMessage 序保证「回包前到达的 outbox 推送都不比快照新」——
 * 主线程每次 open 回包整体替换 outbox 切片即可（entriesFor 本就是全量）。
 */
export interface TimelineOpenResult extends TimelineSlice {
  readonly outbox: OutboxEntry[];
  /**
   * 会话端到端加密态（14.14a）：私聊且本机存有密钥。后续变更只会由本端
   * setEncryptionKey 发起（密钥纯本地），无推送通道——set 回包即新值。
   */
  readonly encryptionOn: boolean;
}

/**
 * 打开中会话的发送状态推送（OutboxEvent 原样跨线程；Worker 侧按 openChatKey
 * 过滤，与 TimelinePush 同口径——登出/关会话不外流）。
 *
 * 主线程合并口径（outbox.ts 文件头的顺序约定）：
 * - upsert：按 clientId 替换或追加（retry 复用 clientId 不换位、UI key 不换）；
 * - sent：**不立即删行**——sent 先于回显行的 timeline append 到达，立删会闪
 *   一帧空档。pending 行保留到 rows 出现同 uuid（=clientId）的服务端行；
 * - removed：用户删除 failed 条目，直接移除。
 */
export type OutboxPush = OutboxEvent;

/**
 * 图片发送受理结果（12b）：accepted=true 时 uploading 乐观条目已入列（entry.msg
 * 为本地 blob URL envelope，entry.localUrl 供回显行继续用本地预览——远端 URL 有
 * Content-Type 矫正窗口期不宜立即 GET）；上传/发帧进展走 'chat/outbox' 推送。
 * 拒绝原因：empty/too-large=大小校验（plan.MAX_UPLOAD_BYTES）；no-session=未登录。
 */
export type ImageSendRpcResult =
  | ImageSendResult
  | { readonly accepted: false; readonly reason: 'no-session' };

// ---------------------------------------------------------------------------
// 方法与推送契约
// ---------------------------------------------------------------------------

export type WorkerMethods = {
  /** 启动读取持久化凭证（纯 IndexedDB，零 HTTP——二次启动不阻塞首屏的前提） */
  'auth/state': { req: null; res: AuthState };
  /** 登录成功即落凭证；need2fa=true 时 UI 跳 TOTP 页带 totpCode 重调（6e） */
  'auth/login': { req: LoginParams; res: LoginRpcResult };
  /** 内部先 ensureIpAnchor（服务端只许本会话请求过 config 的 IP 发码） */
  'auth/sendCode': { req: SendCodeParams; res: null };
  'auth/findPassword': { req: FindPasswordParams; res: null };
  /** 扫码登录：生成二维码会话（§3.7，5 分钟有效，过期由 UI 本地计时判定） */
  'auth/qrGenerate': { req: null; res: QrCodeInfo };
  /**
   * 扫码登录轮询（二维码场景例外允许轮询，2s 间隔归 UI）。confirmed 凭证只能
   * 取一次，Worker 收到即落库。pending 期后端可能瞬时报业务错（AfterLogin 的
   * ip-api 查询失败）——折成 pending 继续轮，过期以 UI 本地计时为准。
   */
  'auth/qrCheck': { req: { readonly qrcodeId: string }; res: QrCheckRpcResult };
  /** 服务端失败不影响本地登出；保留 refreshToken 记录供多账号切回（14.34） */
  'auth/logout': { req: null; res: null };
  /** 已存账号列表（14.34a；纯 IndexedDB 零 HTTP）。当前账号排最前，其余按 uid 升序 */
  'auth/accounts': { req: null; res: { readonly accounts: AccountSummary[] } };
  /**
   * 切换账号（14.34a；旧版流程改造：**先 refresh 校验目标凭证**（失败当前账号
   * 不动）→ 服务端 logout 当前（best-effort 标离线）→ 停 session+清当前账号库
   * （登出同策略，群消息保留）→ loginAs 落新凭证（原账号自动降级只剩
   * refreshToken）→ config 预载 → 启新 session。不拉 profile——主线程到我的
   * Tab 时按需拉（loadMyProfile 既有链）。
   */
  'auth/switch': { req: { readonly userId: number }; res: SwitchAccountRpcResult };
  /** 移除账号记录（14.34a；仅非当前账号——当前账号走 auth/logout）。账号库数据保留（重登秒开） */
  'auth/accounts/remove': { req: { readonly userId: number }; res: boolean };
  /** 首登强制设密码（旧密码空串；成功清 needsPassword 标记）。改密码（带旧密码）走 user/password/change */
  'user/setPassword': { req: { readonly newPassword: string }; res: null };
  /**
   * 我的完整资料（14.29a；GET user/profile 收窄）。Worker 会话期内存缓存：
   * 进我的 Tab ≤1 次、重进零 HTTP（场景 AD）；改资料操作成功后 Worker patch
   * 缓存零回读——页面重调本方法即读新值。null=未登录；失败 reject（UI 重试）。
   */
  /**
   * 我的资料。`force=true` 丢缓存重拉（下拉刷新，21ag-3）；null/false 吃
   * Worker 会话期缓存（场景 AD：进 Tab 恰 1 次，之后零 HTTP）。
   */
  'user/profile/load': { req: { readonly force: boolean } | null; res: MyProfile | null };
  /**
   * 改文本/隐私字段（14.29a；user/change {Field,Value}，avatar 走 user/avatar/change）。
   * false=守门拒零 HTTP（未登录/文本 trim 空——服务端空 Value 报错签名不可清空/
   * 隐私值非数字串）；业务错（username 重复/敏感串等）reject 带服务端文案。
   * Field=nickname 重签 Token Worker 内落位（ChatKey 值不变，§7.4 注），UI 无感。
   */
  'user/profile/change': {
    req: { readonly field: ProfileChangeField; readonly value: string };
    res: boolean;
  };
  /** 换头像（14.29a；压缩管线→uploadv2 Dirname=avatar→change Field=avatar，
   *  场景 AD ≤3 请求）。false=未登录/大小不合规（≤5MB） */
  'user/avatar/change': {
    req: { readonly avatar: { readonly blob: Blob; readonly filename: string } };
    res: boolean;
  };
  /** 改密码（带旧密码；后端拒 new===old 文案直出）。成功 patch hasPassword */
  'user/password/change': {
    req: { readonly oldPassword: string; readonly newPassword: string };
    res: boolean;
  };
  /** 绑手机（验证码预先走 auth/sendCode CodeType=phone_bind，需防水墙）。
   *  重签凭证 Worker 内落位；已绑定过服务端报错（换绑走 user/mobile/*） */
  'user/phone/bind': {
    req: { readonly phone: string; readonly code: string; readonly countryCode: number };
    res: boolean;
  };
  /** 绑邮箱（CodeType=email_bind）。已绑定过报错（换绑走 user/email/change） */
  'user/email/bind': { req: { readonly email: string; readonly code: string }; res: boolean };
  /** 换邮箱（验证码发到**新**邮箱，同 email_bind 场景发码） */
  'user/email/change': { req: { readonly email: string; readonly code: string }; res: boolean };
  /** 换手机四步①：验证码发到当前绑定手机（无 body 免防水墙；仅 +86，未绑手机报错） */
  'user/mobile/sendOldCode': { req: null; res: boolean };
  /** 换手机四步②：旧号验证码 → ChangeToken（5 分钟一次性）。null=守门拒 */
  'user/mobile/verifyOldCode': { req: { readonly code: string }; res: string | null };
  /** 换手机四步③：验证码发新手机号（带 ChangeToken 免防水墙） */
  'user/mobile/sendNewCode': {
    req: { readonly changeToken: string; readonly newPhone: string; readonly countryCode: number };
    res: boolean;
  };
  /** 换手机四步④：确认换绑。重签凭证 Worker 内落位 + patch 缓存零回读 */
  /** 登录设备列表（14.34a；§7.3 只读分页，刻意不缓存——低频管理页）。null=未登录 */
  'user/devices/load': { req: { readonly page: number }; res: DeviceListPage | null };
  /** 2FA 绑定材料（§7.6；不落库，bind 校验通过才生效）。**状态不单独拉**——
   *  profile.google2faEnabled 已有；bind/unbind 成功 Worker patch profile 缓存零回读 */
  'user/2fa/generate': { req: null; res: TwoFaSetup | null };
  'user/2fa/bind': {
    req: { readonly secret: string; readonly code: string };
    res: boolean;
  };
  'user/2fa/unbind': { req: { readonly code: string }; res: boolean };
  /** 我的反馈分页（14.35a；§7.9 id 降序，devices 同口径刻意不缓存）。null=未登录 */
  'user/feedback/list': { req: { readonly page: number }; res: FeedbackListPage | null };
  /** 提交反馈（截图 Blob 跨线程，Worker 逐张 uploadv2 Dirname=chat 后 add_feedback）。
   *  false=守门拒零 HTTP（空标题描述/超 9 图/单图超限）；业务错 reject 文案 UI 直出 */
  'user/feedback/add': { req: SubmitFeedbackInput; res: boolean };
  /** 删除自己的反馈记录（历史页滑动删除）。false=空 ids */
  'user/feedback/remove': { req: { readonly ids: number[] }; res: boolean };
  'user/mobile/confirm': {
    req: {
      readonly changeToken: string;
      readonly newPhone: string;
      readonly countryCode: number;
      readonly code: string;
    };
    res: boolean;
  };
  /** 缓存优先（登录页取 captchaAppId 等） */
  'config/get': { req: null; res: AppConfig };
  /**
   * 弃缓存重取 config（21af）。原本只给路线切换用，现在设置页也要：
   * 运维把 Web Push 通道从 off 改成 on 之后，老用户缓存里的 `webPushPublicKey`
   * 还是空串——这是用户点「推送通知」行时的自助出路，不靠后台轮询发现。
   */
  'config/refresh': { req: null; res: AppConfig };

  /**
   * 上报浏览器推送订阅（按 endpoint upsert；`webpush-backend.md` §3.1）。
   *
   * 主线程负责从 `pushManager` 拿订阅，Worker 负责发带 Token 的请求——
   * `pushManager` 在主线程、http 客户端在 Worker，这条 RPC 就是那道桥。
   * 失败一律抛（调用方把任何失败都当「没上报成功」，不回滚本地订阅）。
   */
  'push/subscribe': { req: PushSubscriptionInput; res: null };
  /**
   * 退订上报（按 endpoint 删行，幂等）。
   *
   * 注意**没有** uid 参数：服务端按 endpoint 删、不校验 user_id，所以这条
   * 在换账号之后发也是安全的。被动退出（凭证已失效）时它必然失败——那条路靠
   * 本地 unsubscribe + 服务端 410 自愈，见 `webpush-backend.md` §5.8。
   */
  'push/unsubscribe': { req: { readonly endpoint: string }; res: null };
  /** 当前访问路线（14.39）。纯 kv 读**恒零 HTTP**，未登录也可调（登录页要显示） */
  'net/route/get': { req: null; res: ApiRouteState };
  /**
   * 切换访问路线（14.39）：落 kv → 弃 config 缓存并重取 → 重连 WS。
   * 返回落位后的状态（即使 `overseas` 与现值相同也返回，幂等）。
   * 值真变了时会发 1 次 GET /front/config（换域名后 Cdn/Ws 必须重取）。
   */
  'net/route/set': { req: { readonly overseas: boolean }; res: ApiRouteState };
  /** 全量本地会话快照（纯 IndexedDB 读零 HTTP——二次启动先渲染本地列表的落点）。
   *  未登录（无 session）返回空行。读走 DB 写串行链，与推送保序（见 ConversationsPush） */
  'chat/conversations': { req: null; res: ConversationsSnapshot };
  /**
   * 手动对账会话列表（21ab，列表头「刷新」钮）：与重连后的自动对账同一条路径。
   * 回包只表示「已受理」，列表变化照常走 'chat/conversations' 推送回流。
   */
  'chat/conversations/resync': { req: null; res: null };
  /**
   * 打开会话并取首屏切片（声明「打开中会话」享受洞补拉待遇，见 session.ts）。
   * 本地有行零 HTTP（场景 D/F）；本地空才 1 次 msg/list（场景 C/E）。洞补拉
   * 刻意排在首屏读之后——先出本地行再 invalidate 找平，不让 HTTP 阻塞首屏。
   * invalidate 后的重读也走本方法（幂等，open 同 key 重调无副作用）。
   * 未登录（无 session）返回空切片（守卫随即会导走，不渲染）。
   */
  'chat/timeline/open': {
    req: { readonly key: ConversationKey; readonly pageSize: number };
    res: TimelineOpenResult;
  };
  /** 向上翻历史（beforeId=已加载最旧行 id）。本地整页零 HTTP，到头记忆见 session */
  'chat/timeline/older': {
    req: { readonly key: ConversationKey; readonly beforeId: number; readonly pageSize: number };
    res: TimelineSlice;
  };
  /** 关闭打开中会话（离开聊天页/登出路由变化时调；无打开中会话时 no-op） */
  'chat/timeline/close': { req: null; res: null };
  /**
   * 乐观发送（outbox.ts 状态机）。返回当下条目（WS 未就绪=failed）；无 session
   * 返回 null（守卫随即导走）。条目状态变化走 'chat/outbox' 推送——回包只用于
   * 立即可用的首个快照（与 upsert 推送幂等，按 clientId 合并）。
   */
  'chat/send': { req: SendInput; res: OutboxEntry | null };
  /**
   * 图片发送（12b）：Blob 经 structuredClone 跨线程零拷贝语义，压缩/MD5/uploadv2
   * 全在 Worker。受理后流程与 chat/send 同（uploading→pending/failed 走推送）；
   * 重试/删除复用 chat/send/retry|discard（Worker 自动分流重新上传或重发帧）。
   * ext（14.1b 相册分段字段，albumModel 口径）原样透传进发送帧。
   */
  'chat/sendImage': {
    req: {
      readonly key: ConversationKey;
      readonly blob: Blob;
      readonly filename: string;
      readonly ext?: string;
    };
    res: ImageSendRpcResult;
  };
  /**
   * 语音发送（14.2b）：录制完成的音频 Blob 原件直传（不压缩不改后缀，codec/
   * 文件名由 blob.type 推导——voiceSend.ts 单点）。受理/推送/重试口径与
   * chat/sendImage 完全一致；durationMs 为录制时长（envelope dur_ms）。
   */
  'chat/sendVoice': {
    req: {
      readonly key: ConversationKey;
      readonly blob: Blob;
      readonly durationMs: number;
    };
    res: ImageSendRpcResult;
  };
  /**
   * 文件/视频发送（14.3b）：原件直传（不压缩不改后缀），envelope 带原始文件名/
   * 大小/mime（fileSend.ts 推导）。fileKind 由主线程按扩展名/MIME 分流（fileSend.
   * attachmentKindFor，video→msgType 7 / file→8）。受理/推送/重试口径与
   * chat/sendImage 完全一致。
   */
  'chat/sendFile': {
    req: {
      readonly key: ConversationKey;
      readonly blob: Blob;
      readonly filename: string;
      readonly fileKind: 'file' | 'video';
    };
    res: ImageSendRpcResult;
  };
  /** 重试 failed 条目（复用 clientId）。非 failed/不存在/无 session 返回 false */
  'chat/send/retry': { req: { readonly clientId: string }; res: boolean };
  /** 删除 failed 条目（结果走 removed 推送） */
  'chat/send/discard': { req: { readonly clientId: string }; res: boolean };
  /**
   * 服务端删除消息（11c；HTTP msg/delete）。私聊物理删双方记录（UI 须强确认），
   * 群聊需管理员+（menuModel 规则把关，恒 myRole=0 前群聊不出删除项）。成功后
   * Worker 本地删行+修会话预览，行退场走 'chat/timeline' remove 推送；HTTP 失败
   * 抛 RpcError（本地不动，UI 提示重试）。无 session 返回 false。
   */
  'chat/delete': {
    req: { readonly key: ConversationKey; readonly msgIds: readonly number[] };
    res: boolean;
  };
  /**
   * 删除群内某成员的全部消息（21j-1，protocol §6.6；群主/管理员）。
   * `kickAndBlack=true` 同时踢出 + 进群黑名单。本地清理以服务端返回的
   * RemovedMsgIds 为准（服务端删整个 Redis 窗口，本地只有已加载的一段）。
   * res = 删掉的条数；null = 非群会话 / 无 session / 已停。
   * 踢人的成员与会话清理走既有 29 信令，不经本回包。
   */
  /**
   * 旧版 Flutter Web 登录态迁移（21k）。主线程从 localStorage 读出旧凭证
   * （store/legacyMigration.ts；Worker 读不到 localStorage）后调这里。
   *
   * Worker 侧用 refreshToken 换一次新 token 做**有效性验证**再落库——旧 token
   * 可能已过期，直接信它会让用户进到一个发什么都 401 的壳子里。
   * res.status：
   * - `migrated` 已登录（含 user 快照），主线程打标记不再重试；
   * - `rejected` 服务端明确拒绝（refreshToken 失效），同样打标记走登录页；
   * - `skipped`  本地已有凭证，什么都没做（打标记）；
   * **网络失败不走这里**，按 RPC 异常抛出，主线程**不打标记**留待下次启动重试。
   */
  /**
   * 永久删除（注销）账号（21e-2，protocol：`POST /front/user/cancel`，无 body）。
   * 成功后本地按**登出且不保留账号记录**收尾（停 session → 清凭证 → 清账号库），
   * 守卫随 auth 置登出自动回登录页。
   * **失败上抛**（与 auth/logout 不同）：注销失败却清了本地，用户会以为账号没了
   * 而实际还在——必须让 UI 报错。
   */
  'auth/cancelAccount': { req: null; res: null };
  /**
   * 清空本地缓存（21ag-4，「我的」→ 清除缓存）：停 session → **整库删除**账号库
   * `lhim-u<uid>` → 丢掉全局库的 config 缓存。Cache Storage（SW 外壳）与重载由
   * 主线程负责（Worker 里 reload 不了页面），单点编排见 store/clearCacheAction.ts。
   *
   * **刻意保留全局库的凭证与 device-id**：两者都在 `lhim-global`，清掉等于强制
   * 登出 / 在后端设备表堆一条新记录（local-state.ts deviceId 注）。所以清完重载
   * 得到的是「已登录的真冷启动」——正是要测的那条路径（content_list + msg/list 全重拉）。
   *
   * ⚠️ 与 auth/logout 的 wipe **不同口径**：这里连群消息一起删。群消息本地是唯一
   * 长期存储（服务端 Redis 每群仅 2000 条，db/cleanup.ts 文件头），超窗口的群历史
   * 删掉永久拉不回来。所以本方法只许挂在显式用户操作上，**不得用于任何自动清理**。
   *
   * 未登录时没有账号库可删，只丢 config 缓存。恒 resolve（清理失败不该把按钮卡死）。
   */
  'app/clearCache': { req: null; res: null };
  'auth/migrateLegacy': {
    req: {
      readonly userId: number;
      readonly userNo: number;
      readonly refreshToken: string;
      readonly deviceId: string;
    };
    res:
      | { readonly status: 'migrated'; readonly user: AuthUser }
      | { readonly status: 'rejected' }
      | { readonly status: 'skipped' };
  };
  'chat/deleteUserMsgs': {
    req: {
      readonly key: ConversationKey;
      readonly userId: number;
      readonly kickAndBlack: boolean;
    };
    res: number | null;
  };
  /**
   * 编辑自己的文本消息（14.7；WS modify 事件，content 裸文本，protocol §5.7）。
   * true=已受理：本地行已乐观替换（msg=裸文本/edited=1），走 'chat/timeline'
   * append 就地替换推送；确认（41/43 信令回播）无感。false=未发出（WS 断/
   * 校验不过/同一消息编辑在途/无 session），本地不动，UI 当场提示。异步失败
   * （error_msg/20s 超时）Worker 回滚原行（append 推送还原）+ 'chat/editFailed'。
   */
  'chat/edit': {
    req: { readonly key: ConversationKey; readonly msgId: number; readonly content: string };
    res: boolean;
  };
  /**
   * 转发（14.8；session.forwardMessages 口径）：无 HTTP 转发接口，对每个目标
   * 按原类型重发 WS 帧（200ms 串行间隔，上限 20 条，降级口径见 forwardModel.ts）。
   * 调用会等整批帧发完才返回（N 帧 ≈ N×200ms）。failed>0 = 有帧未发出
   * （WS 断中止），UI 提示转发失败；skipped = 不可转发被跳过的源消息数。
   */
  'chat/forward': {
    req: {
      readonly sourceKey: ConversationKey;
      readonly msgIds: readonly number[];
      readonly targets: readonly ConversationKey[];
    };
    res: ForwardResult;
  };
  /**
   * 读会话置顶列表（14.10；msgId 升序快照，PinnedMessage 语义见 messages-api）。
   * Worker 按 WS 会话期缓存：命中零 HTTP，未命中 POST msg/pin_list 一次（场景
   * C/E 预算内的第 2 个请求）；重连清缓存。null=拉取失败（UI 无横幅，重开重试）。
   * 后续变更（17/18/21 信令、乐观置顶、回滚）走 'chat/pins' 推送整体替换。
   */
  'chat/pins/load': {
    req: { readonly key: ConversationKey };
    res: readonly PinnedMessage[] | null;
  };
  /**
   * 读群全量成员（14.11a；@面板来源，session.loadGroupMembers 口径）。Worker 按
   * 群缓存 TTL 60s（对齐服务端 1 分钟缓存）：命中零 HTTP，未命中 GET
   * group/list_member 500/页循环（常规群 1 次，场景 P 预算）。null=失败不缓存
   * （面板提示重试）或非群会话。无推送通道——面板每次打开现读，TTL 内零 HTTP。
   */
  'chat/members/load': {
    req: { readonly key: ConversationKey };
    res: readonly GroupMember[] | null;
  };
  /**
   * 读群输入栏限制态（14.15a；session.loadComposer 口径，groupRestrictionModel
   * 推导单点）。Worker 按 WS 会话期缓存：命中零 HTTP，未命中 GET group/profile
   * 一次（场景 E 预算内，14.15b 接线时计入）；禁言信令 12/13/14/15 实时改写
   * 缓存、32 修改群资料失效、重连清空。null=拉取失败不缓存（主线程保持缺省
   * 不限制 fail-open，服务端兜底拒发；下次打开重试）。非群会话返回缺省不限制。
   * 后续变更走 'chat/composer' 推送。
   */
  'chat/composer/load': {
    req: { readonly key: ConversationKey };
    res: ComposerRestriction | null;
  };
  /**
   * 读群资料快照（14.25a；group/profile 全字段收窄，与 'chat/composer/load'
   * **同一份 Worker 会话期缓存**——打开群聊已拉过的进群设置页零 HTTP，requests
   * 场景 Z；失效=25-32 群事件 note/connectok，口径同上）。null=非群会话/拉取
   * 失败不缓存（设置页提示重试）。无推送通道——设置页瞬态现读，变更下次进页
   * 自然新鲜（打开中会话的限制态变更另有 'chat/composer' 推送）。
   */
  'chat/groupProfile/load': {
    req: { readonly key: ConversationKey };
    res: GroupProfileSnapshot | null;
  };
  /**
   * 会话置顶（14.25a；setting/top {ContentId=会话行 contentId}，私聊群聊同
   * 口径）。**无 WS 推送**——成功后会话行本地直改（'chat/conversations' upsert
   * 回流），他端靠下次 content_list 对账。false=本地无行/占位行（contentId=0）。
   */
  'chat/conversation/top': {
    req: { readonly key: ConversationKey; readonly on: boolean };
    res: boolean;
  };
  /**
   * 群免打扰（14.25a；setting/set_no_dist {GroupId}——'chat/friend/noDisturb'
   * 的群变体，11 信令只推自己房间他端找平）。成功后会话行+profile 快照本地直改。
   */
  'chat/group/noDisturb': {
    req: { readonly groupId: number; readonly on: boolean };
    res: boolean;
  };
  /**
   * 群管理写链八件（14.26a；requests 场景 AA：每操作恰 1 请求零回读——成功后
   * Worker 本地直改 profile 快照/会话行/缓存失效，广播信令回流幂等找平）。
   * 业务错一律 reject 带服务端文案（UI 乐观回滚 + toast 直出）；false/null=
   * 入参非法（守门零 HTTP）/无 session。
   */
  /** 改群资料单字段（管理员+；group_name trim 1-30 双守门；notice 归 14.28） */
  'chat/group/change': {
    req: { readonly groupId: number; readonly field: GroupChangeField; readonly value: string };
    res: boolean;
  };
  /** 换群头像（Blob 跨线程；压缩管线 uploadv2 Dirname=groupavatar + change avatar） */
  'chat/group/changeAvatar': {
    req: {
      readonly groupId: number;
      readonly avatar: { readonly blob: Blob; readonly filename: string };
    };
    res: boolean;
  };
  /** 全员禁言（管理员+；setting/stillness 显式值；14/15 广播回流幂等） */
  'chat/group/stillness': {
    req: { readonly groupId: number; readonly on: boolean };
    res: boolean;
  };
  /**
   * 禁止群内私聊（管理员+；setting/allow_private_chat XOR 翻转）。返回服务端
   * 新 AllowChat（0=允许 1=禁止）——UI 乐观翻转后以此找平；null=非法/无 session。
   */
  'chat/group/privateChat': { req: { readonly groupId: number }; res: number | null };
  /** 设置/移除管理员（仅群主；Type "1"/"0"，PermissionList 省略=保留原值） */
  'chat/group/setAdmin': {
    req: { readonly groupId: number; readonly userId: number; readonly isAdmin: boolean };
    res: boolean;
  };
  /** 转让群主（仅群主；原群主降普通成员，打开中会话 composer 当场重拉） */
  'chat/group/transfer': {
    req: { readonly groupId: number; readonly userId: number };
    res: boolean;
  };
  /**
   * 退群（群主不可退服务端拦「你是群主，请先转让群」）。成功后本地清会话+消息
   * +缓存（'chat/conversations' replace 回流；恰是打开中会话另推 timeline reset）。
   */
  'chat/group/quit': { req: { readonly groupId: number }; res: boolean };
  /** 解散群（仅群主物理删除；本地清理同退群） */
  'chat/group/dissolve': { req: { readonly groupId: number }; res: boolean };
  /**
   * 群成员操作四件（14.27a；requests 场景 AB：每操作恰 1 请求零回读——成功后
   * Worker 本地直改成员缓存/快照，session 注）。业务错 reject 带服务端文案；
   * false/null=入参非法（守门零 HTTP）/无 session。
   */
  /** 成员禁言/解禁（管理员+；setting/muted 布尔**无时长**——旧版 6 档是假 UI） */
  'chat/members/mute': {
    req: { readonly groupId: number; readonly userId: number; readonly muted: boolean };
    res: boolean;
  };
  /** 移出成员（管理员+不能移群主；成功本地删行+count-1，不失效重拉） */
  'chat/members/remove': {
    req: { readonly groupId: number; readonly userId: number };
    res: boolean;
  };
  /** 改群内昵称（管理员+；无广播且空串不写库——note trim 非空守门） */
  'chat/members/changeNote': {
    req: { readonly groupId: number; readonly userId: number; readonly note: string };
    res: boolean;
  };
  /**
   * 邀请好友入群（被邀请人必须是我的好友）。'joined'=直接入群（失效成员/快照
   * 缓存现拉）、'pending'=转入待审核（toast「等待管理员审核」，无人入群不失效）。
   */
  'chat/members/invite': {
    req: { readonly groupId: number; readonly userIds: readonly number[] };
    res: InviteMembersResult | null;
  };
  /**
   * 进出群记录一页（14.27a；group/member_log MaxId 游标降序 Limit=20，仅群主/
   * 管理员——普通成员业务错 reject「仅允许群主或管理查看」）。**不缓存**（低频
   * 管理页瞬态）；首页 maxId=0，下页传上页最后行 id，短页即到底。
   */
  'chat/members/log': {
    req: { readonly groupId: number; readonly maxId: number };
    res: readonly GroupMemberLogItem[] | null;
  };
  /**
   * 读贴纸面板数据（14.16a；GET sticker/mine 整包：篮子包+最近发送，场景 M
   * 预算 ≤1）。Worker 双层缓存（内存 TTL 10min + 账号库 kv 暖启动）：命中零
   * HTTP 即时返回；过期旧值照常返回、后台静默刷新（有变走 'chat/stickers'
   * 推送整包替换——无 WS 信令通知包变更，TTL 对账是唯一找平通道）。null=
   * 无缓存且拉取失败（面板空态提示重试）或无 session。
   */
  /**
   * 通讯录三件读（14.18a；Worker WS 会话期缓存命中零 HTTP，requests 场景 K/L；
   * session.loadContacts* 注释）。null=失败/未登录（页面重试）。后续变更
   * （信令局部刷新/失效）走 'chat/contacts' 推送。
   */
  'chat/contacts/book': { req: null; res: ContactsBook | null };
  'chat/contacts/groupChats': { req: null; res: readonly ContactGroupChat[] | null };
  'chat/contacts/points': { req: null; res: ContactPoints | null };
  /**
   * 手动刷新通讯录（21ae 头部刷新钮）：Worker 丢掉三份缓存并推 'chat/contacts'
   * null —— 主线程切片变 null，通讯录页的「null 则 load」自动重拉三件（本调用
   * 自己不发 HTTP，避免与页面并发重复拉）。res 恒 true（无 session 时 false）。
   */
  'chat/contacts/refresh': { req: null; res: boolean };
  /**
   * 服务端搜用户/群（14.19a；friend/search `{Val,Type:1}` / group/search
   * `{Val}` 精确匹配，session.searchUsers 口径）。**不缓存**且必须由 UI 显式
   * 触发（封号级小时限额，requests 场景 T）；归一后空关键词返回 [] 零 HTTP。
   * null=无 session；失败 reject 带服务端文案（含封号提示，直接给用户看）。
   */
  'chat/search/users': { req: { readonly val: string }; res: readonly SearchedUser[] | null };
  'chat/search/groups': { req: { readonly val: string }; res: readonly SearchedGroup[] | null };
  /**
   * 发好友申请（14.19a；friend/add，userNo 只许来自搜索结果）。true=已发出
   * （「已发送申请」是 UI 态，自己侧无信令）；false=入参非法/无 session；
   * 服务端拒绝（重复申请/已是好友/被拉黑/15s 限流）reject 带文案。
   */
  'chat/friend/add': { req: AddFriendInput; res: boolean };
  /** 申请入群（14.19a；group/apply）。口径同 'chat/friend/add'。 */
  'chat/group/apply': { req: GroupApplyInput; res: boolean };
  /**
   * 读申请列表（14.20；requests 场景 U，session.loadApplies 口径）：刻意不缓存
   * （status 要新鲜），每次进页恰 1 请求；拉到后红点本地找平走 'chat/contacts'
   * 推送，不再调 point。null=无 session；失败 reject 带文案（页面错误态直出）。
   */
  'chat/applies/load': { req: { readonly kind: ApplyKind }; res: readonly ContactApply[] | null };
  /**
   * 审核申请（14.20；session.respondApply 口径）：true=已处理（红点本地 -1）；
   * false=入参非法/无 session；业务错 reject 带服务端文案——friend「你们已经是
   * 好友了」路径记录已被置 status=1，调用方业务错后重拉列表找平。
   */
  'chat/applies/respond': { req: RespondApplyInput; res: boolean };
  /**
   * 读好友详情（14.21a；friend/info?FriendId=，requests 场景 V，
   * session.loadFriendProfile 口径）。**刻意不缓存**——开关态要新鲜，每次进页
   * 恰 1 请求（⚠️ 服务端副作用自动建私聊会话，旧版同口径）。null=无 session/
   * 响应不可解析；业务错（非好友禁止查看）reject 带文案。
   */
  'chat/friend/profile': { req: { readonly friendId: number }; res: FriendProfile | null };
  /**
   * 修改好友属性（14.21a；friend/change——备注/记忆/星标/分组，免打扰另走
   * 'chat/friend/noDisturb'）。成功后好友册/会话行本地局部刷新（推送回流），
   * 无 WS 推送（他端下次拉）。业务错（记忆超 500 字等）reject 带文案。
   */
  'chat/friend/change': {
    req: { readonly friendId: number; readonly field: FriendChangeField; readonly value: string };
    res: boolean;
  };
  /**
   * 好友免打扰（14.21a；setting/set_no_dist——同步会话行并推 11 信令他端找平）。
   * 成功后本地直改好友册+会话行（'chat/contacts' + 'chat/conversations' 回流）。
   */
  'chat/friend/noDisturb': {
    req: { readonly friendId: number; readonly on: boolean };
    res: boolean;
  };
  /**
   * 拉黑/解除（14.21a；仅可拉黑好友）。拉黑后会话从双方列表消失（本地删行留
   * 消息）；解除触发一次对账复活会话。业务错 reject 带文案。
   */
  'chat/friend/black': { req: { readonly userId: number; readonly on: boolean }; res: boolean };
  /**
   * 删除好友（14.21a；friend/remove——**双向物理删除**含双方全部私聊消息，UI
   * 必须强确认「聊天记录将被删除」）。成功后好友册/会话/本地消息全清（replace
   * 找平；恰是打开中会话另推 timeline reset）。
   */
  'chat/friend/remove': {
    req: { readonly friendId: number; readonly userNo: number };
    res: boolean;
  };
  /**
   * 删除聊天记录（14.21a 私聊 / 21j-2 群聊同路；scope 两档语义见
   * session.cleanHistory）。入参是会话 key（`dm:<uid>` 或 `group:<gid>`）。false=本地
   * 无会话可删（仅 self 档；UI 提示）；业务错 reject 带文案。
   */
  'chat/history/clean': {
    req: { readonly key: ConversationKey; readonly scope: 'self' | 'both' };
    res: boolean;
  };
  /**
   * 新建好友分组（14.22a；friend_group/add，Sort 恒 0 旧版同口径无拖拽排序）。
   * 成功后本地插组走 'chat/contacts' 推送回流零回读（四个分组写接口都无 WS
   * 推送，他端靠下次 with_friends）。同名报错等业务错 reject 带文案。
   */
  'chat/friendGroup/add': { req: { readonly name: string }; res: boolean };
  /**
   * 重命名分组（14.22a；friend_group/update——name+sort 整体更新，原 sort 由
   * Worker 从 book 读出透传）。false=入参非法/组不在册（他端刚删）/无 session。
   */
  'chat/friendGroup/rename': {
    req: { readonly groupId: number; readonly name: string };
    res: boolean;
  };
  /** 删除分组（14.22a；组下好友服务端同事务归未分组，本地同步搬行推送回流） */
  'chat/friendGroup/delete': { req: { readonly groupId: number }; res: boolean };
  /**
   * 批量移动好友到分组（14.22a；friend_group/move，groupId=0=未分组）。目标组
   * 本地不存在时失效 book 推 null 重拉找平。单好友从详情页移组走
   * 'chat/friend/change'(group_id) 不经此。
   */
  'chat/friendGroup/move': {
    req: { readonly friendIds: readonly number[]; readonly groupId: number };
    res: boolean;
  };
  /**
   * 读黑名单（14.22a；user/black_list 单页 100 旧版同口径）。**刻意不缓存**——
   * 低频管理页且拉黑态要新鲜，每次进页恰 1 请求（透传不进 GlobalState）。移出
   * 复用 'chat/friend/black' {on:false}（含 content_list 对账复活会话）。
   */
  'chat/blacklist/load': { req: null; res: readonly BlackListUser[] | null };
  /**
   * 读系统公告（14.24；GET front/announcement，文案后端写死免登录无分页）。
   * **刻意不缓存**（blacklist 同口径：低频页面瞬态组件态），进页恰 1 请求
   * （requests §3 场景 Y）。null=无 session；失败 reject 带服务端文案。
   */
  'chat/announcements/load': { req: null; res: readonly AnnouncementItem[] | null };
  /**
   * 建群（14.23a；POST group/create {FriendId,Name,Avatar}，protocol §9.3）。
   * avatar 可选（Blob 跨线程结构化克隆，chat/sendImage 先例）：Worker 先走
   * 压缩管线 uploadv2（Dirname=groupavatar）再建群。成功返回群 id（UI 据此
   * openChat）；会话行本地直插/群聊 Tab 失效走 'chat/conversations'/
   * 'chat/contacts' 推送回流零回读。null=入参非法（群名 trim 后 1-30/成员
   * ≥1/头像 ≤5MB）/无 session；业务错（免费群上限/非双向好友等）reject 带
   * 服务端文案。
   */
  'chat/group/create': {
    req: {
      readonly name: string;
      readonly friendIds: readonly number[];
      readonly avatar?: { readonly blob: Blob; readonly filename: string };
    };
    res: number | null;
  };
  'chat/stickers/load': { req: null; res: StickerMineSnapshot | null };
  /**
   * 记录一次贴纸发送（14.16a；发帧成功即报不等回显，旧版 recordSent 同时机）：
   * Worker 本地 recent 去重置顶 ≤20（kv 持久化 + 'chat/stickers' 推送回流，
   * 主线程无需乐观直改）+ 异步上报 POST sticker/recent（失败静默丢弃）。
   * false=入参非法/无 session。
   */
  'chat/stickers/recordSent': { req: RecentSticker; res: boolean };
  /**
   * 我创建的包（14.17a；GET sticker/created 全部状态看审核进度，新在前）。
   * 刻意不缓存——管理页低频且审核状态要新鲜，每次进页一拉。null=失败/无
   * session（页面重试钮再调）。
   */
  'chat/stickers/created': { req: null; res: readonly CreatedStickerPack[] | null };
  /**
   * 包详情（14.17a；聊天点别人的贴纸→整包预览）。篮子命中零 HTTP（mine 快照
   * 本地直出 added=true）；未命中 GET sticker/pack 一次。null=失败/无 session。
   */
  'chat/stickers/packDetail': {
    req: { readonly packId: number };
    res: { pack: CreatedStickerPack; added: boolean } | null;
  };
  /**
   * 加入篮子（14.17a；POST sticker/add，要求已过审）。成功后 Worker 本地追加
   * mine 快照（不重拉）走 'chat/stickers' 推送回流。req 要完整包形态（调用方
   * 从 created/详情拿到，basketPackOf 折）。false=入参非法/无 session；服务端
   * 拒绝（未过审等）reject 带文案。
   */
  'chat/stickers/addPack': { req: StickerPack; res: boolean };
  /** 移出篮子（14.17a）。成功后本地删 mine 快照推送回流；失败 reject 带文案。 */
  'chat/stickers/removePack': { req: { readonly packId: number }; res: boolean };
  /**
   * 创建者删包（14.17a；任何状态，服务端连带所有人篮子引用）。本地同步删
   * mine 快照推送回流；失败 reject 带文案。
   */
  'chat/stickers/deletePack': { req: { readonly packId: number }; res: boolean };
  /**
   * 上传单张表情（14.17a；multipart ≤100KB 原样直传）。预检（扩展名/大小）
   * 归 UI 层 stickerFileRejection，服务端兜底。null=无 session；失败 reject
   * 带服务端文案（「单张表情不能超过 100KB」等）。
   */
  'chat/stickers/upload': {
    req: { readonly blob: Blob; readonly filename: string };
    res: StickerDraftItem | null;
  };
  /**
   * 提交表情包审核（14.17a；无 packId=create / 带 packId=update 重审，仅待审/
   * 被拒的包）。res=包 id；null=无 session；失败 reject 带服务端文案（「待审核
   * 的表情包过多」等）。
   */
  'chat/stickers/submitPack': { req: StickerPackDraft; res: number | null };
  /**
   * 置顶/取消置顶（14.10；WS pin 事件，protocol §5.7，session.pinMessage 口径）。
   * true=已发出且本地已乐观应用（行 pin 标记走 'chat/timeline' append 就地替换、
   * 置顶列表走 'chat/pins' 推送）；false=未发出（WS 断/行不存在/已一致/在途/无
   * session），本地不动。异步失败（error_msg，uuid 恒空仅唯一在途可关联）Worker
   * 回滚 + 'chat/pinFailed'。
   */
  'chat/pin': {
    req: { readonly key: ConversationKey; readonly msgId: number; readonly pin: boolean };
    res: boolean;
  };
  /**
   * 设置阅后即焚秒数（14.13a；仅私聊，second=0 关闭，HTTP set_after_view）。
   * true=服务端已改（本地会话 afterViewTime 直改走 'chat/conversations' upsert
   * 推送，33 信令幂等找平）；false=HTTP 失败/无 session，本地不动（调用方
   * toast 重试）。
   */
  'chat/afterView/set': {
    req: { readonly friendId: number; readonly second: number };
    res: boolean;
  };
  /**
   * 设置/清除私聊端到端加密密钥（14.14a；session.setChatKey 口径）：key=用户明文
   * 输入（Worker 内 SHA-256 派生后存账号库 kv，明文不出 Worker 不落盘）、null=
   * 关闭删除。零 HTTP。res=设置后的加密态。恰是打开中会话时 Worker 顺带推
   * timeline invalidate——主线程重读即拿到按新密钥解密的行。
   */
  'chat/encryption/set': {
    req: { readonly friendId: number; readonly key: string | null };
    res: boolean;
  };
  /**
   * 已读通知（10c，fire-and-forget）：主线程报「打开中会话已看到 msgId」
   * （最后一条对方消息 id，messagesState 触发口径）。Worker 当场清本地未读
   * （upsert 走 'chat/conversations' 推送）并防抖上报 WS read 帧（session.noteRead）。
   */
  'chat/read': { req: { readonly key: ConversationKey; readonly msgId: number }; res: null };
  /**
   * 回前台探活（13a，requests 场景 I/J）：主线程 visibilitychange/pageshow 回
   * 前台时调。alive=true = WS 确认存活（零 HTTP，场景 I 预算 0）；alive=false =
   * 已触发立即重连（跳过剩余退避），后续 connectok→content_list 对账→打开中
   * 会话洞补拉在 Worker 内自动跟进（场景 J ≤2），结果走 'chat/conversations'
   * replace 与 'chat/timeline' invalidate 推送——主线程只管调，无需按返回值行动。
   * 未登录（无 session）返回 alive=false。
   */
  'chat/wake': { req: null; res: { readonly alive: boolean } };
  /**
   * 冷启动恢复点（13b）：启动读「上次打开的会话 + 滚动 anchor」（纯 IndexedDB
   * 零 HTTP；未登录/没有恢复点返回 null）。恢复点由 Worker 随 timeline open/close
   * 自动记/清（登出清库即消失），主线程只在冷启动根路径 + 首个 auth 决议即登录态
   * 时取用（restoreState.ts 口径）。
   */
  'chat/restore/get': { req: null; res: RestorePoint | null };
  /**
   * 页面隐藏（visibilitychange hidden / pagehide，覆盖 standalone 进程回收前的
   * 最后时机）时回传打开中会话的滚动 anchor，补进恢复点。anchor 对 Worker 不透明
   * （virtualizer 序列化形态）；非打开中会话的回传忽略（切换竞态）。
   */
  'chat/restore/anchor': {
    req: { readonly key: ConversationKey; readonly anchor: RestoreAnchor };
    res: null;
  };
  /**
   * 草稿整批快照（14.12）：登录后一次读出（纯 IndexedDB 零 HTTP），装饰会话
   * 列表 [草稿] + bump 排序。后续变更由主线程乐观维护 + 'chat/draft/save' 回写，
   * 无推送通道（草稿只有主线程会改；删会话附带清草稿时行本身已消失，无需找平）。
   * 未登录（无 session）返回空。
   */
  'chat/drafts': { req: null; res: { readonly rows: readonly DraftRecord[] } };
  /**
   * 草稿写入（14.12，fire-and-forget）：输入防抖即存、空文本（text=''）即清。
   * updatedAt 毫秒时间戳由主线程盖章——列表 bump 排序与主线程乐观切片用同一
   * 时间，两端不分叉。
   */
  'chat/draft/save': {
    req: { readonly key: ConversationKey; readonly text: string; readonly updatedAt: number };
    res: null;
  };
  /**
   * 归档键整批快照（14.36）：登录后一次读出（纯 IndexedDB 零 HTTP），会话列表
   * 据此把归档会话从主列表分流到归档入口。归档是**纯本地标记不跨端同步**
   * （旧版同口径，legacy §5.2），所以没有服务端字段、没有推送通道——后续变更
   * 由主线程乐观维护 + 'chat/archive/set' 回写。未登录（无 session）返回空。
   */
  'chat/archived': { req: null; res: { readonly keys: readonly ConversationKey[] } };
  /**
   * 归档写入（14.36，fire-and-forget 同草稿）：archivedAt=毫秒时间戳归档、
   * null=取消归档。删会话时 cleanup 连带删标记，无需找平。
   */
  'chat/archive/set': {
    req: { readonly key: ConversationKey; readonly archivedAt: number | null };
    res: null;
  };
};

export type WorkerPushes = {
  /** 凭证确认失效（Worker 已清凭证）。主线程置 logged-out，守卫回登录页（6d） */
  'auth/lost': { readonly reason: AuthLostReason };
  /**
   * 应用配置变更（21ah）：冷启动后台软刷新 / 切线路 / 设置页重取，**取回来值真
   * 变了**才推（同值不惊动主线程）。主线程整体替换 config 切片——cdn 一换，
   * 头像图片等 resolveMediaUrl 拼出来的 URL 立刻跟着走，不用等下次登录。
   * Ws 变更的重连在 Worker 内连带做掉，主线程不用管。
   */
  'config/changed': AppConfig;
  /** 会话列表变更（WS 应用批 / 对账快照），语义见 ConversationsPush */
  'chat/conversations': ConversationsPush;
  /** 打开中会话的时间线变更（新消息批 / 洞补拉完成），语义见 TimelinePush */
  'chat/timeline': TimelinePush;
  /** 打开中会话的发送状态变更（pending/failed/回显解决/删除），语义见 OutboxPush */
  'chat/outbox': OutboxPush;
  /**
   * 编辑异步失败（14.7；仅打开中会话外流）：原行已回滚（先行的 timeline append
   * 已就地替换还原），本推送只供 UI toast（messagesState.editFailedSeq）。
   */
  'chat/editFailed': {
    readonly key: ConversationKey;
    readonly msgId: number;
    /** 服务端 error_msg 原文（21f）；空串=超时等无文案路径，UI 回落「编辑失败」 */
    readonly message: string;
  };
  /**
   * 置顶列表整体替换（14.10；仅打开中会话外流）：信令/乐观操作/回滚驱动。
   * 首次读走 'chat/pins/load' 回包，此推送只负责后续变更。
   */
  'chat/pins': { readonly key: ConversationKey; readonly pins: readonly PinnedMessage[] };
  /**
   * 批量转发进度（21u，人工要求「超过 5 个人要加上进度显示」）。
   * 转发是 **N×200ms 串行发帧**（服务端不接受帧风暴），目标多时要等好几秒；
   * `chat/forward` 回包要等整批发完才回，中途没有任何反馈。
   * 这条推送在每发出一帧后外流一次 `{sent,total}`，UI 据此画进度。
   * 不带 key：转发是跨会话动作，与「打开中会话」无关。
   */
  'chat/forwardProgress': { readonly sent: number; readonly total: number };
  /** 置顶操作异步失败（14.10；行已回滚，append 推送已还原）：仅供 UI toast */
  'chat/pinFailed': { readonly key: ConversationKey; readonly msgId: number };
  /**
   * 无归属的服务端错误（21f，旧版 _handleRealtimeError 的 uuid 为空那一支）：
   * error_msg 既没匹配上在途发送/编辑、也没回滚在途置顶，此前整条丢弃用户全无感知。
   * **不按打开中会话过滤**（这类错误与会话无关），主线程弹 toast 原文展示。
   * message 恒非空（Worker 侧已过滤空文案）；鉴权段 40100~40200 不走这里。
   */
  'chat/serverError': { readonly code: number; readonly message: string };
  /**
   * 输入栏限制态变更（14.15a；仅打开中会话外流）：禁言信令/群资料变更驱动。
   * 首次读走 'chat/composer/load' 回包，此推送只负责后续变更。
   */
  'chat/composer': { readonly key: ConversationKey; readonly restriction: ComposerRestriction };
  /**
   * 贴纸面板数据整包替换（14.16a；账号全局，不随打开中会话过滤）：后台刷新
   * 有变/本地最近记录更新驱动。首次读走 'chat/stickers/load' 回包。
   */
  'chat/stickers': StickerMineSnapshot;
  /**
   * 通讯录变更（14.18a；字段级合并：带哪个字段替换哪个切片，null=失效清空——
   * 主线程切片清 null 后通讯录页打开中自动重 load，contactsModel.ContactsPush）。
   * 首次读走 'chat/contacts/*' 回包，此推送只负责后续变更。
   */
  'chat/contacts': ContactsPush;
  /**
   * 好友上下线（14.38）：WS `presence` 事件帧级合并后的一批变化（后到覆盖先到）。
   * **只有推送通道、没有首次读 RPC**——事实源就是这条推送流本身（服务端无在线
   * 快照接口，presenceModel 文件头），主线程 map 自登录起累积。
   */
  'chat/presence': { readonly entries: readonly PresenceEntry[] };
};

// ---------------------------------------------------------------------------
// RpcError code ↔ 业务错误映射
// ---------------------------------------------------------------------------

const API_PREFIX = 'api/';
const NET_PREFIX = 'net/';

export function apiErrorRpcCode(code: number): string {
  return API_PREFIX + code;
}

export function networkErrorRpcCode(kind: string): string {
  return NET_PREFIX + kind;
}

/** RpcError.code → 业务错误码；非 api/ 前缀（网络/RPC 自身错误）返回 undefined */
export function parseApiErrorCode(rpcCode: string): number | undefined {
  if (!rpcCode.startsWith(API_PREFIX)) return undefined;
  const n = Number(rpcCode.slice(API_PREFIX.length));
  return Number.isSafeInteger(n) ? n : undefined;
}

/** 该错误是否网络类（可提示「网络异常请重试」，与业务失败区分） */
export function isNetworkRpcCode(rpcCode: string): boolean {
  return rpcCode.startsWith(NET_PREFIX);
}
